import React, { useEffect, useMemo, useState } from 'react'
import { __ } from '@wordpress/i18n'
import { Button } from '../../../components/Button/Button'
import thankYouStyles from '../ThankYou/ThankYou.module.scss'
import styles from './PaymentHandler.module.scss'
import {
    Elements,
    CardElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { store_name } from '../../../../store/frontend'
import { useSelect } from '@wordpress/data'
import { BookingBlock } from '../../../components/BookingBlock/BookingBlock'
import { wbkFormatPrice } from '../../../providers/BookingFormProvider/utils'
import { FormNotice } from '../../../components/FormNotice/FormNotice'
import classNames from 'classnames'
import iconCheck from '../../../../../public/images/icon-check.svg'
import { useBookingContext } from '../../../providers/BookingFormProvider/BookingFormProvider'
import { useWording } from '../../../hooks/useWording'
import { CustomScroll } from 'react-custom-scroll'
import { fromUnixTime } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

const StripeForm: React.FC<{
    onPaid?: () => void
    paymentDetails: any
    clientSecret: string
    setError: (e: string) => void
    setLoading: (b: boolean) => void
}> = ({ onPaid, paymentDetails, clientSecret, setError, setLoading }) => {
    const stripe = useStripe()
    const elements = useElements()
    const [localLoading, setLocalLoading] = useState(false)
    const wording = useWording()

    // Don't render early - let Stripe load first
    if (!stripe || !elements) {
        return (
            <div style={{ color: '#888', marginTop: 8 }}>
                {wording?.loading_payment_form ||
                    __('Loading payment form...', 'webba-booking-lite')}
            </div>
        )
    }

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        setError('')
        setLocalLoading(true)
        setLoading(true)

        const cardElement = elements.getElement(CardElement)
        if (!cardElement) {
            setError('Card element not found')
            setLocalLoading(false)
            setLoading(false)
            return
        }

        if (!clientSecret) {
            setError('No client secret provided')
            setLocalLoading(false)
            setLoading(false)
            return
        }

        try {
            // Confirm card payment with Stripe
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            })

            if (result.error) {
                setError(result.error.message || 'Payment failed')
                setLocalLoading(false)
                setLoading(false)
                return
            }

            // Submit payment intent result to backend (using the new method)
            try {
                const paymentIntentId = result?.paymentIntent?.id
                const paymentMethodId = result?.paymentIntent?.payment_method
                if (!paymentIntentId) {
                    setError('No payment intent ID returned from Stripe')
                    setLocalLoading(false)
                    setLoading(false)
                    return
                }
                // @ts-ignore
                const response = await window.wp?.data
                    ?.dispatch(store_name)
                    ?.submitStripePayment(paymentIntentId, paymentMethodId)
                if (response && !response.error) {
                    onPaid && onPaid()
                } else {
                    setError(response?.error || 'Payment failed')
                }
            } catch (err) {
                setError('Payment failed')
            }
        } catch (err) {
            setError('Payment failed')
        }

        setLocalLoading(false)
        setLoading(false)
    }

    return (
        <form onSubmit={handleSubmit} className={styles.stripeForm}>
            <div className={styles.stripeElementsWrapper}>
                <CardElement options={{ hidePostalCode: true }} />
            </div>
            <Button
                type="primary"
                onClick={handleSubmit}
                showLoading={localLoading}
                disabled={localLoading}
            >
                {localLoading
                    ? wording?.processing ||
                      __('Processing...', 'webba-booking-lite')
                    : wording?.pay || __('Pay', 'webba-booking-lite')}
            </Button>
        </form>
    )
}

export const PaymentHandler: React.FC<{
    bookingData: any
    paymentMethods?: any[]
    onPaid?: () => void
}> = ({ bookingData, paymentMethods: propPaymentMethods, onPaid }) => {
    const [paymentSuccess, setPaymentSuccess] = useState(false)
    const [buttonLoading, setButtonLoading] = useState(false)
    const [buttonError, setButtonError] = useState<string>('')
    const wording = useWording()

    if (!bookingData) return null

    const paymentMethod = bookingData.payment_method
    const paymentUrl = bookingData.payment_url
    const paymentDetails = bookingData.payment_details
    const paymentRequired = bookingData.payment_required
    const bookingTimes = bookingData.times

    // Get paymentMethods and priceFormat from context, but prefer prop
    const {
        paymentMethods: contextPaymentMethods,
        priceFormat,
        services,
        amountData,
        preset,
        timezone,
    } = useBookingContext()
    const paymentMethods = propPaymentMethods || contextPaymentMethods

    const paymentMethodObj = paymentMethods.find(
        (pm: { name: string; id: string }) =>
            pm.name === paymentMethod || pm.id === paymentMethod
    )
    const paymentIcon = paymentMethodObj?.icon || ''
    const paymentTitle = paymentMethodObj
        ? (wording?.pay_with || __('Pay with', 'webba-booking-lite')) +
          ' ' +
          (paymentMethodObj.name.charAt(0).toUpperCase() +
              paymentMethodObj.name.slice(1))
        : wording?.payment || __('Payment', 'webba-booking-lite')

        console.log(bookingData)
    // Booking items for BookingBlock
    const bookingItems = useMemo<{ time: Date; serviceName: string }[]>(() => {
        if (!paymentDetails?.item_names) return []
        // If you have time/serviceName info, map it here. Otherwise, just show names.
        return paymentDetails.item_names.map((name: string, idx: number) => ({
            // time: new Date(), // fallback to now if no time info
            time: toZonedTime(fromUnixTime(bookingTimes[idx] || 0), timezone),
            serviceName: name,
        }))
    }, [paymentDetails])

    // Cost breakdown (dual logic)
    let costItems: {
        name: string
        price: number
        priceFromAmountData: number
        priceFallback: number
    }[] = []
    let totalAmount = 0
    let tax = 0
    if (paymentDetails && paymentDetails.item_names && paymentDetails.prices) {
        // Use paymentDetails if present (legacy/confirmed bookings)
        const names: string[] = paymentDetails.item_names || []
        const prices: (string | number)[] = paymentDetails.prices || []
        const quantities: (string | number)[] = paymentDetails.quantities || []
        costItems = names.map((name: string, idx: number) => {
            const price = Number(prices[idx] || 0)
            const quantity = Number(quantities[idx] || 1)
            const totalPrice = price * quantity
            return {
                name,
                price: totalPrice,
                priceFromAmountData: totalPrice,
                priceFallback: totalPrice,
            }
        })
        totalAmount = paymentDetails.total || 0
        tax =
            paymentDetails.tax_to_pay != null
                ? paymentDetails.tax_to_pay
                : paymentDetails.tax || 0
    } else {
        // Use dual calculation logic for selected services
        const selectedServices = services.filter((s: any) => s.selected)
        costItems = selectedServices.map((service: any) => {
            const matchedAmountItems = amountData.items.filter(
                (item: any) => item.id === service.id
            )
            const amountDataPrice = matchedAmountItems.reduce(
                (sum: number, item: any) => sum + Number(item.price || 0),
                0
            )
            const servicePrice = Number(service.price) || 0
            const quantity = Number(service.quantity) || 1
            const numPlaces = Array.isArray(service.places)
                ? service.places.length
                : 1
            const fallbackPrice = servicePrice * quantity * numPlaces
            return {
                name: service.label,
                price: amountDataPrice > 0 ? amountDataPrice : fallbackPrice,
                priceFromAmountData: amountDataPrice,
                priceFallback: fallbackPrice,
            }
        })
        totalAmount =
            amountData.total && Number(amountData.total) > 0
                ? Number(amountData.total)
                : costItems.reduce((sum, item) => sum + item.priceFallback, 0)
        tax = amountData.tax || 0
    }

    // Stripe - simplified like the old version
    const stripePublishableKey = useSelect(
        (select: any) =>
            select(store_name).getPreset()?.settings?.stripe_publishable_key,
        []
    )

    const stripePromise = stripePublishableKey
        ? loadStripe(stripePublishableKey)
        : null

    // Use clientSecret from bookingData if present, otherwise from store
    const clientSecret =
        bookingData.client_secret ||
        useSelect(
            (select: any) => select(store_name).getBookingData()?.client_secret,
            []
        )

    // Handle payment
    const handlePay = async () => {
        setButtonLoading(true)
        setButtonError('')
        try {
            if (paymentMethod === 'paypal' && paymentUrl) {
                window.open(paymentUrl, '_blank')
                setButtonLoading(false)
                return
            }
            if (paymentMethod === 'woocommerce' && paymentUrl) {
                window.location.href = paymentUrl
                setButtonLoading(false)
                return
            }
            // For pay_on_arrival or bank, simulate success
            if (
                paymentMethod === 'pay_on_arrival' ||
                paymentMethod === 'bank'
            ) {
                setTimeout(() => {
                    setPaymentSuccess(true)
                    onPaid && onPaid()
                }, 1000)
                return
            }
            // For Stripe, handled in StripeForm
            // For other methods, simulate success
            setTimeout(() => {
                setPaymentSuccess(true)
                onPaid && onPaid()
            }, 1000)
        } catch (e: any) {
            setButtonError(e?.message || 'Unknown error')
        } finally {
            setButtonLoading(false)
        }
    }

    return (
        <div className={'wbk_thank_you__wrapper'}>
            <CustomScroll heightRelativeToParent="Calc(750px - 64px)">
                <div className={'wbk_thank_you__inner-wrapper'}>
                    {/* Header always visible, animate only the success state */}
                    <div className={'wbk_thank_you__header'}>
                        {!paymentSuccess && (
                            <>
                                <img
                                    src={paymentIcon}
                                    alt={paymentMethod}
                                    style={{ width: 56, height: 56 }}
                                />
                                <div
                                    className={'wbk_thank_you__header__content'}
                                >
                                    <h3>{paymentTitle}</h3>
                                    <p>
                                        {wording?.please_complete_payment_confirm_booking ||
                                            __(
                                                'Please complete your payment to confirm your booking.',
                                                'webba-booking-lite'
                                            )}
                                    </p>
                                </div>
                            </>
                        )}
                        {paymentSuccess && (
                            <div className={'wbk_thank_you__header--visible'}>
                                <img
                                    src={iconCheck}
                                    alt={
                                        wording?.payment_successful ||
                                        __(
                                            'Payment successful',
                                            'webba-booking-lite'
                                        )
                                    }
                                />
                                <div
                                    className={'wbk_thank_you__header__content'}
                                >
                                    <h3>
                                        {wording?.payment_successful ||
                                            __(
                                                'Payment Successful',
                                                'webba-booking-lite'
                                            )}
                                    </h3>
                                    <p>
                                        {wording?.thank_you_payment ||
                                            __(
                                                'Thank you for your payment.',
                                                'webba-booking-lite'
                                            )}
                                    </p>
                                </div>
                                {paymentSuccess &&
                                    bookingData?.ical_url &&
                                    preset?.is_pro === true && (
                                        <Button
                                            title={
                                                wording?.add_to_calendar ||
                                                __(
                                                    '+ Add to Calendar',
                                                    'webba-booking-lite'
                                                )
                                            }
                                            href={bookingData.ical_url}
                                            target="_blank"
                                        />
                                    )}
                            </div>
                        )}
                    </div>
                    {/* Booking Items */}
                    <div className={'wbk_thank_you__booking_items'}>
                        {bookingItems.map(
                            (
                                {
                                    time,
                                    serviceName,
                                }: { time: Date; serviceName: string },
                                idx: number
                            ) =>
                                costItems[idx]?.price > 0 && (
                                    <BookingBlock
                                        key={idx}
                                        time={Number(
                                            Math.floor(time.getTime() / 1000)
                                        )}
                                        serviceName={serviceName}
                                    />
                                )
                        )}
                    </div>
                    {/* Cost Breakdown */}
                    <div className={'wbk_thank_you__panel-wrapper'}>
                        <h4>
                            {wording?.cost_breakdown ||
                                __('Cost Breakdown', 'webba-booking-lite')}
                        </h4>
                        <div className={'wbk_thank_you__cart-items'}>
                            {costItems.map((item, idx) => (
                                <div
                                    className={
                                        'wbk_thank_you__cart-items__item'
                                    }
                                    key={idx}
                                >
                                    <p>{item.name}</p>
                                    <strong>
                                        {item.priceFromAmountData > 0
                                            ? wbkFormatPrice(
                                                  item.priceFromAmountData,
                                                  priceFormat
                                              )
                                            : wbkFormatPrice(
                                                  item.priceFallback,
                                                  priceFormat
                                              )}
                                    </strong>
                                </div>
                            ))}
                            {typeof amountData.tax === 'number' &&
                                amountData.tax > 0 && (
                                    <div
                                        className={classNames(
                                            'wbk_thank_you__cart-items__item',
                                            'wbk_thank_you__cart-items__item--tax'
                                        )}
                                    >
                                        <h4>
                                            {wording?.tax ||
                                                __('Tax', 'webba-booking-lite')}
                                        </h4>
                                        <strong>
                                            {wbkFormatPrice(
                                                amountData.tax,
                                                priceFormat
                                            )}
                                        </strong>
                                    </div>
                                )}
                            <div
                                className={classNames(
                                    'wbk_thank_you__cart-items__item',
                                    'wbk_thank_you__cart-items__item--total'
                                )}
                            >
                                <h4>
                                    {wording?.total_amount_paid ||
                                        __(
                                            'Total amount paid',
                                            'webba-booking-lite'
                                        )}
                                </h4>
                                <strong>
                                    {wbkFormatPrice(totalAmount, priceFormat)}
                                </strong>
                            </div>
                        </div>
                    </div>
                    {/* Pay Button or Stripe Form */}
                    {!paymentSuccess && (
                        <div
                            className={
                                'wbk_landing_page__action-button-container'
                            }
                        >
                            {(() => {
                                if (
                                    paymentMethod === 'stripe' &&
                                    paymentRequired &&
                                    stripePromise &&
                                    clientSecret
                                ) {
                                    return (
                                        <Elements stripe={stripePromise}>
                                            <StripeForm
                                                onPaid={() => {
                                                    setPaymentSuccess(true)
                                                    onPaid && onPaid()
                                                }}
                                                paymentDetails={paymentDetails}
                                                clientSecret={clientSecret}
                                                setError={setButtonError}
                                                setLoading={setButtonLoading}
                                            />
                                        </Elements>
                                    )
                                } else if (
                                    paymentMethod === 'woocommerce' &&
                                    paymentUrl
                                ) {
                                    // Redirect to WooCommerce payment URL
                                    window.location.href = paymentUrl
                                    return null
                                } else {
                                    return (
                                        <Button
                                            type="primary"
                                            title={
                                                wording?.pay ||
                                                __('Pay', 'webba-booking-lite')
                                            }
                                            showLoading={buttonLoading}
                                            onClick={handlePay}
                                            disabled={buttonLoading}
                                        />
                                    )
                                }
                            })()}
                            {buttonError && (
                                <span
                                    className={
                                        'wbk_landing_page__action-button-error'
                                    }
                                >
                                    {buttonError}
                                </span>
                            )}
                        </div>
                    )}
                    {wording?.after_booking_instructions && (
                        <FormNotice className={'wbk_thank_you__information'}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: wording?.after_booking_instructions,
                                }}
                            />
                        </FormNotice>
                    )}
                </div>
            </CustomScroll>
        </div>
    )
}
